import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import type { LegalDoc } from '@/mocks/legalCenter';
import { legalCenterDocs, LEGAL_COLORS, getActiveVersion } from '@/mocks/legalCenter';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
}

const iconMap: Record<string, string> = {
  'ri-file-text-line': 'ri-file-text-line',
  'ri-shield-check-line': 'ri-shield-check-line',
  'ri-advertisement-line': 'ri-advertisement-line',
  'ri-percent-line': 'ri-percent-line',
  'ri-store-2-line': 'ri-store-2-line',
  'ri-key-2-line': 'ri-key-2-line',
};

export default function LegalCenterPage() {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const toggleExpand = (doc: LegalDoc) => {
    setExpandedId(expandedId === doc.id ? null : doc.id);
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      {/* Hero */}
      <section className="relative pt-20 pb-14 md:pt-28 md:pb-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=abstract%20minimalist%20legal%20hub%20concept%20with%20warm%20beige%20cream%20and%20soft%20charcoal%20geometric%20flowing%20shapes%2C%20elegant%20scales%20of%20justice%20metaphor%20in%20abstract%20form%2C%20subtle%20paper%20texture%20background%2C%20modern%20professional%20legal%20center%20aesthetic%2C%20clean%20lines%20and%20curves%2C%20light%20airy%20atmosphere%2C%20no%20text%20no%20logos%2C%20graceful%20abstract%20composition%20with%20subtle%20gold%20accents&width=1600&height=600&seq=legal-center-hero-03&orientation=landscape)`,
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/75 via-background-50/92 to-background-50"></div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-foreground-100 text-foreground-600 text-xs font-semibold mb-5">
            <i className="ri-scales-3-line"></i>
            Documentación Legal
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3">Centro Legal</h1>
          <p className="text-foreground-500 text-sm max-w-xl mx-auto">
            Todos los documentos legales de RadioFrecuenciaWeb en un solo lugar. Transparencia, claridad y acceso permanente.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="flex-1 pb-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          {/* Intro card */}
          <div className="mb-8 p-5 bg-background-100/70 rounded-2xl border border-background-200/70 flex flex-col sm:flex-row items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-foreground-950 flex items-center justify-center shrink-0">
              <i className="ri-information-line text-background-50 text-xl"></i>
            </div>
            <div>
              <h2 className="text-foreground-900 font-bold text-sm mb-1">Versiones vigentes</h2>
              <p className="text-foreground-500 text-xs leading-relaxed">
                Todos los documentos mostrados en este Centro Legal son las versiones actualmente vigentes y publicadas. RadioFrecuenciaWeb mantiene un historial completo de versiones para fines de auditoría y transparencia. Cualquier modificación a estos documentos es registrada y queda disponible en el historial de cambios.
              </p>
            </div>
          </div>

          {/* Documents grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {legalCenterDocs.map((doc) => {
              const active = getActiveVersion(doc);
              const color = LEGAL_COLORS[doc.color];
              const isExpanded = expandedId === doc.id;

              return (
                <div
                  key={doc.id}
                  className={`bg-background-50 border rounded-2xl transition-all duration-300 overflow-hidden ${
                    isExpanded
                      ? `${color.border} shadow-sm md:col-span-2 lg:col-span-3`
                      : 'border-background-200/70 hover:border-background-300'
                  }`}
                >
                  {/* Card header */}
                  <div
                    onClick={() => toggleExpand(doc)}
                    className="flex items-start gap-3 p-4 cursor-pointer group"
                  >
                    <div className={`w-10 h-10 rounded-xl ${color.iconBg} flex items-center justify-center shrink-0 mt-0.5 transition-transform group-hover:scale-105`}>
                      <i className={`${doc.icon} ${color.iconText} text-base`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-foreground-900 font-semibold text-sm group-hover:text-foreground-950 transition-colors">
                          {doc.title}
                        </h3>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-bold ${color.bg} ${color.text}`}>
                          <span className={`w-1 h-1 rounded-full ${color.dot}`}></span>
                          v{active.versionNumber}
                        </span>
                      </div>
                      <p className="text-foreground-400 text-[10px] mt-0.5 line-clamp-2">{doc.description}</p>
                      <div className="flex items-center gap-3 mt-1.5 text-foreground-300 text-[9px]">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line text-[9px]"></i>
                          Actualizado: {formatDate(doc.lastPublishedAt)}
                        </span>
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded-lg bg-background-100 flex items-center justify-center shrink-0 transition-transform ${
                      isExpanded ? 'rotate-180' : ''
                    }`}>
                      <i className="ri-arrow-down-s-line text-foreground-500 text-sm"></i>
                    </div>
                  </div>

                  {/* Expanded content */}
                  {isExpanded && (
                    <div className="px-4 pb-5 pt-0 border-t border-background-100">
                      <div className="mt-4 bg-background-100/60 rounded-xl p-5 md:p-6">
                        <div
                          className="prose prose-sm max-w-none text-foreground-700 legal-preview"
                          dangerouslySetInnerHTML={{ __html: active.content }}
                        ></div>
                      </div>
                      <div className="mt-3 flex items-center justify-between text-foreground-400 text-[10px]">
                        <span className="flex items-center gap-1">
                          <i className="ri-information-line text-[9px]"></i>
                          Versión {active.versionNumber} · Vigente desde {formatDate(active.publishedAt || doc.lastPublishedAt)}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line text-[9px]"></i>
                          {active.createdBy}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Bottom info */}
          <div className="mt-12 p-6 bg-background-100/70 rounded-2xl border border-background-200/70">
            <div className="flex flex-col sm:flex-row items-start gap-5">
              <div className="w-12 h-12 rounded-xl bg-foreground-100 flex items-center justify-center shrink-0">
                <i className="ri-history-line text-foreground-600 text-xl"></i>
              </div>
              <div>
                <h3 className="text-foreground-900 font-bold text-sm mb-2">Historial y Control de Versiones</h3>
                <p className="text-foreground-500 text-xs leading-relaxed">
                  Cada documento legal de RadioFrecuenciaWeb está sujeto a un riguroso sistema de control de versiones. Cada modificación genera una nueva versión numerada que queda registrada con fecha, autor y notas de cambio. La versión activa es la que se muestra en este Centro Legal y es la jurídicamente vinculante. Las versiones anteriores se conservan archivadas para auditoría. Para acceder al historial completo de cualquier documento, contactanos en <strong>legal@radiofrecuenciaweb.com</strong>.
                </p>
              </div>
            </div>
          </div>

          {/* Quick links */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/terms"
              className="px-5 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-1.5"></i>
              Términos y Condiciones
            </Link>
            <Link
              to="/privacy"
              className="px-5 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-shield-check-line mr-1.5"></i>
              Política de Privacidad
            </Link>
            <Link
              to="/cookies"
              className="px-5 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-settings-line mr-1.5"></i>
              Política de Cookies
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}